/**
 * Current quality settings, shared between the governor (inside the Canvas),
 * PostFX (rebuilds on MSAA/bloom changes) and the HUD. A tiny external store for
 * useSyncExternalStore: no React state per frame, no new dependency.
 */
import { flags } from '../config/flags'
import { ladderAt, type QualitySettings } from './quality'

type Listener = () => void

/** Manual flags (?scale ?msaa ?bloomres) pin the settings and switch auto off. */
export const manualOverride =
  flags.scale !== null || flags.msaa !== null || flags.bloomRes !== null

/** null = auto. A number = fixed ladder level (?quality=3, or 0 with manual flags). */
export const fixedLevel: number | null = manualOverride ? 0 : flags.quality === 'auto' ? null : flags.quality

function settingsFor(level: number): QualitySettings {
  const base = ladderAt(level)
  if (!manualOverride) return base
  return {
    ...base,
    scale: flags.scale ?? base.scale,
    msaa: flags.msaa ?? base.msaa,
    bloomRes: flags.bloomRes ?? base.bloomRes,
  }
}

let level = fixedLevel ?? 0
let current: QualitySettings = settingsFor(level)
const listeners = new Set<Listener>()

export const qualityStore = {
  get: () => current,
  level: () => level,
  setLevel(next: number) {
    if (next === level) return
    level = next
    current = settingsFor(next)
    listeners.forEach((l) => l())
  },
  subscribe(l: Listener) {
    listeners.add(l)
    return () => listeners.delete(l)
  },
}

export { settingsFor }
